import { useFeatureFlags } from './flags';

/**
 * The settings a distributor's integration is set up around, and the two flags
 * that decide who may touch them.
 *
 * Both flags are the legacy admin's, by name and by meaning:
 * `integratedSupplierMarkingUserFlow` decides whether the guard exists,
 * `adminCanEditSupplierIntegrationSettings` decides whether the person looking
 * may change the settings. Legacy computed `flag && supplierIsIntegrated` in
 * every place that needed it; the helpers below do it in one.
 */

/**
 * Exactly the set the legacy admin disabled once a distributor was integrated:
 * `ConfigurationSectionInputSet.tsx` for the first three and
 * `DisplayToCustomersFormField.tsx` for the fourth.
 *
 * Mandatory delivery date is deliberately absent. The legacy drawer listed it
 * among the five it displayed, but the real input was disabled unconditionally
 * for every distributor, integrated or not, under an announcement that it is now
 * true for all of them. It is not an integration setting, and gating it here
 * would be inventing a rule.
 */
export const INTEGRATION_CONTROLLED_FIELDS = [
  'allowCustomProducts',
  'mandatoryCustomerNumber',
  'enableProductComments',
  'displayToCustomers',
] as const;

export type IntegrationControlledField =
  (typeof INTEGRATION_CONTROLLED_FIELDS)[number];

/** Whether `field` is one the integration was set up around. */
export const isIntegrationControlledField = (
  field: string
): field is IntegrationControlledField =>
  (INTEGRATION_CONTROLLED_FIELDS as readonly string[]).includes(field);

/** The flag that has to be on for someone to change these settings. */
export const INTEGRATION_SETTINGS_FLAG =
  'adminCanEditSupplierIntegrationSettings' as const;

export interface IntegrationSettingsAccess {
  /** `integratedSupplierMarkingUserFlow`: is the guard switched on at all. */
  isGuardEnabled: boolean;
  /** `adminCanEditSupplierIntegrationSettings`: may this person change them. */
  canChange: boolean;
}

export interface TestDistributorFlags {
  isPuppy?: boolean | null;
  isTest?: boolean | null;
}

export const useIntegrationSettingsAccess = (): IntegrationSettingsAccess => {
  const flags = useFeatureFlags();

  return {
    isGuardEnabled: flags.integratedSupplierMarkingUserFlow,
    canChange: flags[INTEGRATION_SETTINGS_FLAG],
  };
};

/**
 * Dogfooding distributors count as test distributors for this gate too.
 *
 * The lock exists to stop accidental changes on live integrations. Test and
 * puppy distributors are the exception: the portal should let anyone editing the
 * distributor change these settings inline, even when the record is marked
 * integrated.
 */
export const isTestDistributor = ({
  isPuppy,
  isTest,
}: TestDistributorFlags): boolean => Boolean(isPuppy || isTest);

/**
 * Whether the settings are read-only on the form.
 *
 * True for everyone once the distributor is integrated, including anyone who may
 * change them. That is the legacy rule and it is deliberate: the inputs live on
 * three different tabs, and a setting editable in three places at once is a
 * setting nobody can reason about. Changing them happens in one place instead.
 *
 * Test distributors are exempt. They are where these settings are tried out and
 * corrected, so the portal keeps them editable inline even when the record is
 * marked integrated.
 */
export const isIntegrationLocked = (
  access: IntegrationSettingsAccess,
  isIntegrated: boolean,
  distributor: TestDistributorFlags
): boolean =>
  access.isGuardEnabled && isIntegrated && !isTestDistributor(distributor);

/**
 * Whether a locked field must also be dropped from the save payload.
 *
 * Narrower than the screen lock: someone who may change these settings does it
 * through the one place that offers it, and that edit has to reach the backend.
 * For anyone else the field is dropped, because a disabled control is only a
 * suggestion and every value still reaches the payload builder.
 */
export const isIntegrationPayloadLocked = (
  access: IntegrationSettingsAccess,
  isIntegrated: boolean,
  distributor: TestDistributorFlags
): boolean =>
  isIntegrationLocked(access, isIntegrated, distributor) && !access.canChange;
